import React, { useState, useEffect } from 'react';
import { Grid3X3, Users, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { StaffAllocationGrid, MultiGridView } from '../../types/grid';
import { Staff } from '../../types/data';
import { useMultiGrid } from '../../hooks/useMultiGrid';
import StaffAllocationGridComponent from './StaffAllocationGrid';
import { SubjectLineAvailability, LineSubjectConfig, createSubjectAvailabilityMap } from '../../types/lineSubject';
import { availabilityFromClasses, hasClassLineAvailability } from '../../utils/gridSetup';
import { supabase } from '../../supabase/config';
import { MultiGridErrorBoundary } from './MultiGridErrorBoundary';

interface CombinedGridViewProps {
  staff: Staff[];
  onAssignTeacher?: (classId: string, lineId: string, teacherCode: string, gridId: string) => void;
  className?: string;
}

function CombinedGridViewInner({ staff, onAssignTeacher, className = '' }: CombinedGridViewProps) {
  // Enhanced error handling for context data
  let allGrids: StaffAllocationGrid[] = [];
  let multiGridView: MultiGridView = { activeGridIds: [], viewMode: 'tabs' };
  
  try {
    const contextData = useMultiGrid();
    allGrids = Array.isArray(contextData?.allGrids) ? contextData.allGrids : [];
    multiGridView = contextData?.multiGridView || { activeGridIds: [], viewMode: 'tabs' };
  } catch (error) {
    // Continue with safe defaults
  }
  
  const [collapsedGrids, setCollapsedGrids] = useState<Set<string>>(new Set());
  
  // Subject-line availability data for each grid
  const [gridSubjectAvailability, setGridSubjectAvailability] = useState<Record<string, SubjectLineAvailability>>({});
  const [gridLoadingStates, setGridLoadingStates] = useState<Record<string, boolean>>({});
  const [gridErrors, setGridErrors] = useState<Record<string, string>>({});

  // Enhanced defensive programming with comprehensive null/undefined checks
  const safeAllGrids = Array.isArray(allGrids) ? allGrids : [];
  const safeMultiGridView = multiGridView || { activeGridIds: [], viewMode: 'tabs' as const };
  const safeActiveGridIds = (
    Array.isArray(safeMultiGridView.activeGridIds) && 
    typeof safeMultiGridView.activeGridIds.includes === 'function'
  ) ? safeMultiGridView.activeGridIds : [];
  
  const visibleGrids = safeAllGrids.filter(grid => {
    try {
      // Enhanced validation for grid structure
      const isValidGrid = grid &&
                         typeof grid === 'object' &&
                         grid.id &&
                         typeof grid.id === 'string' &&
                         typeof grid.name === 'string' &&
                         Array.isArray(grid.classes) &&
                         Array.isArray(grid.lines) &&
                         safeActiveGridIds.includes(grid.id);

      if (!isValidGrid) {
        console.warn('CombinedGridView: Invalid grid structure:', {
          hasId: !!grid?.id,
          hasName: !!grid?.name,
          hasClasses: Array.isArray(grid?.classes),
          hasLines: Array.isArray(grid?.lines),
          grid: grid
        });
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  });

  // Load subject-line availability data for all visible grids
  useEffect(() => {
    const loadSubjectLineAvailabilityForGrids = async () => {
      
      if (!Array.isArray(visibleGrids) || visibleGrids.length === 0) {
        setGridSubjectAvailability({});
        setGridLoadingStates({});
        setGridErrors({});
        return;
      }

      // Initialize loading states
      const loadingStates: Record<string, boolean> = {};
      visibleGrids.forEach(grid => {
        loadingStates[grid.id] = true;
      });
      setGridLoadingStates(loadingStates);
      setGridErrors({});

      const availabilityData: Record<string, SubjectLineAvailability> = {};
      const errors: Record<string, string> = {};

      for (const grid of visibleGrids) {
        try {
          // Prefer per-class availability (allowedLineIds); fall back to the
          // legacy line_subject_config table for older grids.
          if (hasClassLineAvailability(grid.classes)) {
            availabilityData[grid.id] = availabilityFromClasses(grid.classes);
            continue;
          }

          // Load line-subject configuration from Supabase
          const { data: configData, error } = await supabase
            .from('line_subject_config')
            .select('*')
            .eq('grid_id', grid.id)
            .single();
            
          if (error) {
            if (error.code === 'PGRST116') {
              // No data found - this is expected for new grids
              availabilityData[grid.id] = {};
            } else {
              // Actual error occurred
              errors[grid.id] = `Failed to load line scheduling: ${error.message}`;
              availabilityData[grid.id] = {};
            }
          } else if (configData?.mappings && Array.isArray(configData.mappings) && configData.mappings.length > 0) {
            const availabilityMap = createSubjectAvailabilityMap(configData.mappings);
            availabilityData[grid.id] = availabilityMap;
          } else {
            availabilityData[grid.id] = {};
          }
        } catch (error) {
          errors[grid.id] = `Failed to load line scheduling: ${error instanceof Error ? error.message : 'Unknown error'}`;
          availabilityData[grid.id] = {};
        }
      }

      setGridSubjectAvailability(availabilityData);
      setGridErrors(errors);
      
      // Clear all loading states
      const clearedLoadingStates: Record<string, boolean> = {};
      visibleGrids.forEach(grid => {
        clearedLoadingStates[grid.id] = false;
      });
      setGridLoadingStates(clearedLoadingStates);
    };

    loadSubjectLineAvailabilityForGrids();
  }, [visibleGrids.length, JSON.stringify(safeActiveGridIds)]); // Safer dependency array

  const toggleGridCollapse = (gridId: string) => {
    const newCollapsed = new Set(collapsedGrids);
    if (newCollapsed.has(gridId)) {
      newCollapsed.delete(gridId);
    } else {
      newCollapsed.add(gridId);
    }
    setCollapsedGrids(newCollapsed);
  };

  if (visibleGrids.length === 0) {
    return (
      <div className={`p-8 text-center ${className}`}>
        <Grid3X3 className="h-12 w-12 text-ink-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-ink-900 mb-2">No Grids Selected</h3>
        <p className="text-ink-500">
          Select grids from the tabs above to view them in the combined view.
        </p>
      </div>
    );
  }

  const calculateGridStats = (grid: StaffAllocationGrid) => {
    // Defensive programming: Ensure grid and grid.classes exist
    if (!grid || !Array.isArray(grid.classes)) {
      return {
        totalClasses: 0,
        assignedClasses: 0,
        uniqueTeachers: 0,
        completionPercentage: 0
      };
    }

    const totalClasses = grid.classes.length;
    const assignedClasses = grid.classes.filter(c => 
      c && c.lineAssignments && Object.values(c.lineAssignments).some(teacher => teacher)
    ).length;
    
    const uniqueTeachers = new Set(
      grid.classes
        .filter(c => c && c.lineAssignments)
        .flatMap(c => Object.values(c.lineAssignments || {}))
        .filter(Boolean)
        .flatMap(teacherCode => {
          // Handle multi-teacher assignments
          if (teacherCode && typeof teacherCode === 'string' && (teacherCode.startsWith('[') || teacherCode.startsWith('{'))) {
            try {
              const parsed = JSON.parse(teacherCode);
              if (Array.isArray(parsed)) {
                return parsed.map(t => t?.teacherCode).filter(Boolean);
              } else if (parsed?.teacherCode) {
                return [parsed.teacherCode];
              }
            } catch (e) {
              // If parsing fails, use original teacherCode
            }
          }
          return [teacherCode];
        })
        .filter(Boolean)
    ).size;

    return {
      totalClasses,
      assignedClasses,
      uniqueTeachers,
      completionPercentage: totalClasses > 0 ? Math.round((assignedClasses / totalClasses) * 100) : 0
    };
  };

  return (
    <div className={`bg-ink-50 ${className}`}>
      <div className="max-w-[95vw] mx-auto py-6 space-y-8">
        {visibleGrids.map((grid, index) => {
          // Defensive programming: Skip invalid grids
          if (!grid || !grid.id || !grid.name) {
            console.warn('CombinedGridView: Skipping invalid grid:', grid);
            return null;
          }

          const stats = calculateGridStats(grid);
          const isCollapsed = collapsedGrids.has(grid.id);
          const isLoading = gridLoadingStates[grid.id];
          const error = gridErrors[grid.id];
          
          return (
            <div key={grid.id} className="bg-white rounded-lg shadow-sm border">
              {/* Grid Header */}
              <div 
                className={`px-6 py-4 bg-ink-50 cursor-pointer hover:bg-ink-100 transition-colors ${
                  isCollapsed ? 'rounded-lg' : 'border-b rounded-t-lg'
                }`}
                onClick={() => toggleGridCollapse(grid.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {isCollapsed ? (
                        <ChevronRight size={20} className="text-ink-500" />
                      ) : (
                        <ChevronDown size={20} className="text-ink-500" />
                      )}
                      <Grid3X3 size={20} className="text-navy-600" />
                      <h2 className="text-xl font-semibold text-ink-900">{grid.name}</h2>
                      {isLoading && (
                        <div className="flex items-center gap-1 text-navy-600">
                          <div className="w-3 h-3 border border-navy-600 border-t-transparent rounded-full animate-spin" />
                          <span className="text-xs">Loading...</span>
                        </div>
                      )}
                      {error && (
                        <span className="text-xs text-danger-fg bg-danger-bg px-2 py-1 rounded">
                          Line schedule error
                        </span>
                      )}
                    </div>
                    
                    {grid.defaultClassMinutes && (
                      <span className="font-mono tabular-nums px-3 py-1 bg-navy-100 text-navy-700 text-sm rounded-full">
                        {grid.defaultClassMinutes}min classes
                      </span>
                    )}
                    
                    {!grid.isActive && (
                      <span className="px-3 py-1 bg-danger-bg text-danger-fg text-sm rounded-full">
                        Inactive
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-6 text-sm text-ink-500">
                    <div className="flex items-center gap-1">
                      <Grid3X3 size={14} />
                      <span className="font-mono tabular-nums">{stats.totalClasses} classes</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users size={14} />
                      <span className="font-mono tabular-nums">{stats.uniqueTeachers} teachers</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span className="font-mono tabular-nums">{stats.completionPercentage}% assigned</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Grid Content - Only render when not collapsed */}
              {!isCollapsed && (
                <div className="p-6">
                  {!Array.isArray(grid.classes) || grid.classes.length === 0 ? (
                    <div className="text-center py-12 text-ink-500">
                      <Grid3X3 className="h-12 w-12 text-ink-300 mx-auto mb-4" />
                      <p>No classes in this grid</p>
                    </div>
                  ) : (
                    <MultiGridErrorBoundary
                      fallback={
                        <div className="text-center py-12 text-ink-500">
                          <Grid3X3 className="h-12 w-12 text-ink-300 mx-auto mb-4" />
                          <p>Error loading grid: {grid.name}</p>
                          <button 
                            onClick={() => window.location.reload()} 
                            className="mt-2 px-4 py-2 bg-navy-500 text-white rounded hover:bg-navy-600"
                          >
                            Reload Grid
                          </button>
                        </div>
                      }
                    >
                      <StaffAllocationGridComponent
                        grid={grid}
                        staff={Array.isArray(staff) ? staff : []}
                        subjectLineAvailability={gridSubjectAvailability[grid.id] || {}}
                        onAssignTeacher={(classId, lineId, teacherCode) => {
                          // Enhanced validation for teacher assignment with cross-grid checks
                          if (!classId || !lineId || !grid.id) {
                            console.error('CombinedGridView: Invalid assignment parameters:', {
                              classId,
                              lineId,
                              gridId: grid.id,
                              teacherCode
                            });
                            return;
                          }

                          // Validate class exists in grid
                          const classExists = grid.classes?.some(cls => cls?.id === classId);
                          if (!classExists) {
                            console.error('CombinedGridView: Class not found in grid:', {
                              classId,
                              gridId: grid.id,
                              gridName: grid.name,
                              availableClasses: grid.classes?.map(c => c?.id).slice(0, 5)
                            });
                            return;
                          }

                          // Validate line exists in grid with enhanced checking
                          const lineExists = grid.lines?.some(line => {
                            return typeof line === 'string' ? line === lineId : line?.id === lineId;
                          });
                          if (!lineExists) {
                            console.error('CombinedGridView: Line not found in grid:', {
                              lineId,
                              gridId: grid.id,
                              gridName: grid.name,
                              availableLines: grid.lines?.map(l => typeof l === 'string' ? l : l?.id),
                              expectedLineStructure: typeof grid.lines?.[0]
                            });
                            return;
                          }

                          // Check for cross-grid conflicts before assignment
                          if (teacherCode && visibleGrids.length > 1) {
                            const conflictingGrids = visibleGrids.filter(otherGrid => {
                              if (otherGrid.id === grid.id) return false;
                              return otherGrid.classes?.some(cls =>
                                cls.lineAssignments?.[lineId] === teacherCode
                              );
                            });

                            if (conflictingGrids.length > 0) {
                              console.warn('CombinedGridView: Cross-grid conflict detected:', {
                                teacherCode,
                                lineId,
                                conflictingGrids: conflictingGrids.map(g => g.name)
                              });
                              // Still proceed but warn in console
                            }
                          }

                          onAssignTeacher?.(classId, lineId, teacherCode, grid.id);
                        }}
                      />
                    </MultiGridErrorBoundary>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Main component with comprehensive error boundary
export function CombinedGridView(props: CombinedGridViewProps) {
  return (
    <MultiGridErrorBoundary
      fallback={
        <div className="p-8 text-center bg-white rounded-lg shadow-sm border">
          <Grid3X3 className="h-12 w-12 text-ink-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-ink-900 mb-2">Multi-Grid View Error</h3>
          <p className="text-ink-500 mb-4">
            There was an error loading the multi-grid view. This is usually temporary.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-navy-500 text-white rounded hover:bg-navy-600"
          >
            Reload Page
          </button>
        </div>
      }
      onError={(error, errorInfo) => {
        console.error('Enhanced Multi-Grid Editor Error:', { error, errorInfo });
        // Enhanced error tracking
        if (error.message.includes('includes') || error.message.includes('activeGridIds')) {
          console.error('Multi-grid state corruption detected:', {
            error: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack
          });
        }
      }}
    >
      <CombinedGridViewInner {...props} />
    </MultiGridErrorBoundary>
  );
}